import React, { useState, useRef, useEffect, useCallback } from "react";
import "@fortawesome/fontawesome-free/css/all.min.css";
import Remainder from "../Remainder.tsx";
import apiService from "../../services/api";

const LeadCard = ({ lead, activeId, setActiveId, handleChangeLeadData }) => {
  const {
    id,
    name,
    messageTime,
    message,
    missedMassages,
    answerType,
    role,
    notification,
    parsedNotificTime,
    rawLead,
  } = lead;

  const sourceLabel = 'FUB';
  const isConverted = String((lead?.stage || '').toString().toLowerCase()) === 'converted'
    || String((lead?.status || '').toString().toLowerCase()) === 'converted';
  // Use the AI-classified stage (passed as 'role' prop) directly from API response
  const roleLabel = role || 'Lead';
  
  // State to store insights fetched from API
  const [insights, setInsights] = useState<Record<string, any>>({});
  
  // Fetch insights when component mounts or lead.id changes
  const fetchInsights = useCallback(async () => {
    if (!id) return;
    try {
      const fetchedInsights = await apiService.getLeadInsights(String(id));
      setInsights(fetchedInsights || {});
    } catch (err) {
      console.warn(`[LeadCard] Failed to load insights for lead ${id}:`, err);
      // Keep empty insights object on error
      setInsights({});
    }
  }, [id]);

  useEffect(() => {
    fetchInsights();
  }, [fetchInsights]);

  // Listen for conversations fetched event to refresh insights and update count
  useEffect(() => {
    const handler = (evt: Event) => {
      const custom = evt as CustomEvent<{ leadId?: string | number; timestamp?: number }>;
      const evtLeadId = custom?.detail?.leadId == null ? undefined : String(custom.detail.leadId);
      
      // Only refresh if this event is for the current lead
      if (!id || !evtLeadId || evtLeadId !== String(id)) return;

      // Refresh insights when conversations are fetched to update filled fields count
      fetchInsights();
    };

    window.addEventListener("myhomey:conversations-fetched", handler as EventListener);
    return () => {
      window.removeEventListener("myhomey:conversations-fetched", handler as EventListener);
    };
  }, [id, fetchInsights]);
  
  // Count how many information fields have data
  // Use insights data (from API) first, then fallback to lead/rawLead data (similar to LeadContent)
  const countFilledInformationFields = () => {
    const informationFields = [
      'motive',
      'buyOrSell',
      'timeframe',
      'agentStatus',
      'firstTimeBuyer',
      'income',
      'homeValue',
      'creditScore',
      'cashOrMortgage',
      'employment',
      'otherHomes',
    ];
    
    let count = 0;
    for (const field of informationFields) {
      // Check insights first (from API), then lead, then rawLead (same priority as LeadContent)
      const value = insights[field] ?? (lead as any)?.[field] ?? (rawLead as any)?.[field];
      // Check if value exists and is not empty
      const hasValue = typeof value === 'boolean' 
        ? true 
        : Boolean(value !== undefined && value !== null && value !== '' && String(value).trim());
      
      if (hasValue) {
        count++;
      }
    }
    
    return count;
  };
  
  const filledFieldsCount = countFilledInformationFields();
  const [remainderOpen, setRemainderOpen] = useState(false);
  const bellRef = useRef(null);

  return (
    <>
      <div
        onClick={() => {
          setActiveId(id.toString());
          handleChangeLeadData(id, "missedMassages", 0);
        }}
        className={`relative px-5 py-4 rounded-2xl bg-lightestGray border hover:border-darkBlue cursor-pointer group ${
          activeId.toString() === id.toString()
            ? "border-darkBlue"
            : "border-lightGray"
        }`}
      >
        <div className="flex justify-between mb-2">
          <p className="flex gap-x-2 items-center text-h3 font-medium text-pureBlack">
            <span>{name}</span>
            {isConverted && (
              <span className="ml-1 px-2 py-[2px] text-[11px] leading-[14px] font-bold rounded-[10px] bg-[#E6F5EC] text-[#1F7A3E] border border-[#BFE6CC]">
                Converted
              </span>
            )}
            {notification ? (
              <i
                ref={bellRef}
                className="fa-solid fa-bell text-darkBlue"
                onClick={(e) => {
                  e.stopPropagation();
                  setRemainderOpen(true);
                }}
              ></i>
            ) : (
              <i
                ref={bellRef}
                className="fa-regular fa-bell text-gray opacity-0 group-hover:opacity-100 hover:text-darkBlue"
                onClick={(e) => {
                  e.stopPropagation();
                  setRemainderOpen(true);
                }}
              ></i>
            )}
          </p>
          <span className="text-h4 font-regular text-darkGray">
            {messageTime}
          </span>
        </div>
        <div
          className={`mb-3 pb-3 border-b border-lightestBlue ${
            !!missedMassages ? "flex gap-x-3" : ""
          }`}
        >
          <p className="text-h3 font-medium text-darkGray mh-text-cut">
            {message}
          </p>
          {!!missedMassages && activeId !== id && (
            <span className="px-3 text-h3 font-bold text-lightestGray bg-darkerNavy h-[22px] rounded-[32px]">
              {missedMassages}
            </span>
          )}
        </div>
        <div className="flex justify-between gap-x-3 overflow-x-auto mh-scroll">
          <button className="min-w-fit h-6 px-3 text-h4 font-bold text-darkGray bg-lightestBlue rounded-[32px]">
            {answerType}
          </button>
          <button className="min-w-fit h-6 px-3 text-h4 font-bold text-darkGray bg-lightestBlue rounded-[32px]">
            {roleLabel}
          </button>
          <button className="min-w-fit h-6 px-3 text-h4 font-bold text-darkGray bg-lightestBlue rounded-[32px]">
            {sourceLabel}
          </button>
          {/* {isConverted && (
            <button className="min-w-fit h-6 px-3 text-h4 font-bold text-[#1F7A3E] bg-[#E6F5EC] border border-[#BFE6CC] rounded-[32px]">
              Converted
            </button>
          )} */}
          <button className="min-w-fit flex items-center gap-x-2 h-6 px-3 text-h4 font-bold text-darkGray bg-lightestBlue rounded-[32px]">
            <i className="fa-solid fa-trophy"></i>
            <span>{filledFieldsCount}</span>
          </button>
        </div>
      </div>
      {remainderOpen && (
        <Remainder
          isOpen={remainderOpen}
          notification={notification}
          onClose={() => setRemainderOpen(false)}
          anchorRef={bellRef}
          handleChangeLeadData={handleChangeLeadData}
          leadId={id}
          parsedNotificTime={parsedNotificTime}
        />
      )}
    </>
  );
};

export default LeadCard;
